/**
 * Site settings: the two things that are the platform's own, on tabs.
 *
 * Branding and AI capabilities have nothing to do with each other beyond both
 * being site-wide, and AI needs a whole screen per feature - stacking them on
 * one page buried the AI switches under a pair of logo cards.
 *
 * The tab lives in the URL (?tab=ai) so "look at the rephrasing setup" is a link
 * someone can send.
 */

import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { ApiError, api } from '../../lib/api/client'
import { Icon } from '../../components/icons'
import { Button, HeaderMetrics, PageHeader, Pill, useTabList } from '../../components/ui'
import { toast } from '../../components/toast'
import { AiCapabilities } from './AiCapabilities'
import { SiteRetentionSettings } from '../retention/RetentionSettings'
import { SiteEmailSettings } from '../email/EmailSettings'
import './schools.css'

type Tab = 'branding' | 'ai' | 'email' | 'retention'

const TABS: [Tab, string][] = [
  ['branding', 'Branding'],
  ['ai', 'AI capabilities'],
  ['email', 'Email'],
  ['retention', 'Retention'],
]

export function SiteSettings() {
  const tabs = useTabList()
  const [params, setParams] = useSearchParams()
  const requested = params.get('tab') as Tab | null
  const [fallback, setFallback] = useState<Tab>('branding')
  const tab: Tab = requested && TABS.some(([key]) => key === requested) ? requested : fallback

  const setTab = (next: Tab) => {
    setFallback(next)
    const updated = new URLSearchParams(params)
    updated.set('tab', next)
    setParams(updated, { replace: true })
  }

  return (
    <div className="tc-page tc-site-settings">
      <PageHeader
        eyebrow="Platform administration"
        title="Site settings"
        icon={<Icon.settings size={20} />}
        description="Control the TeachCraftAI identity, the AI features available to every school, and how notifications are sent."
        actions={
          <HeaderMetrics
            label="Settings scope"
            items={[{ value: 'Site-wide', label: 'Affects every school' }]}
          />
        }
      />

      {/* The same bar the school settings screen uses (features/school/school.css):
          a surfaced strip, so the page tabs sit on something and are clearly the
          outer level above the feature switcher inside AI Capabilities. */}
      <div className="tc-settings-tabs tc-site-settings-tabs">
        <div className="tc-tabs" ref={tabs.ref} onKeyDown={tabs.onKeyDown} role="tablist" aria-label="Site settings sections">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              id={`tc-site-${key}-tab`}
              role="tab"
              aria-controls={`tc-site-${key}-panel`}
              aria-selected={tab === key}
              className={`tc-tab ${tab === key ? 'is-active' : ''}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div
        id={`tc-site-${tab}-panel`}
        role="tabpanel"
        aria-labelledby={`tc-site-${tab}-tab`}
        className="tc-site-settings-content"
      >
        {tab === 'branding' && <BrandingControls />}
        {tab === 'ai' && <AiCapabilities />}
        {tab === 'email' && <SiteEmailSettings />}
        {tab === 'retention' && <SiteRetentionSettings />}
      </div>
    </div>
  )
}

function BrandingControls() {
  const queryClient = useQueryClient()
  const logoInput = useRef<HTMLInputElement>(null)
  const faviconInput = useRef<HTMLInputElement>(null)
  const branding = useQuery({
    queryKey: ['platform', 'branding'],
    queryFn: api.branding,
  })
  const update = useMutation({
    mutationFn: ({ kind, file }: { kind: 'logo' | 'favicon'; file: File }) =>
      api.uploadBranding(kind, file),
    onSuccess: async (result) => {
      queryClient.setQueryData(['platform', 'branding'], result)
      toast('Site branding updated.')
      window.setTimeout(() => window.location.reload(), 150)
    },
    onError: (error) =>
      toast(
        error instanceof ApiError
          ? error.message
          : 'The branding image could not be saved.',
        'error',
      ),
  })
  const restore = useMutation({
    mutationFn: api.restoreBranding,
    onSuccess: async (result) => {
      queryClient.setQueryData(['platform', 'branding'], result)
      toast('Default branding restored.')
      window.setTimeout(() => window.location.reload(), 150)
    },
    onError: (error) =>
      toast(
        error instanceof ApiError
          ? error.message
          : 'The default branding could not be restored.',
        'error',
      ),
  })

  const choose = (kind: 'logo' | 'favicon', files: FileList | null) => {
    const file = files?.[0]
    if (file) update.mutate({ kind, file })
  }

  return (
    <section
      className="tc-branding"
      aria-labelledby="site-branding-title"
      aria-busy={update.isPending || restore.isPending}
    >
      <div className="tc-branding-heading">
        <div>
          <h2 id="site-branding-title">Platform branding</h2>
          <p>Keep the product recognisable in navigation, sign-in, and browser tabs.</p>
        </div>
        <Pill tone="secondary">PNG · 2 MB maximum</Pill>
      </div>
      <div className="tc-branding-grid">
        <article className={`tc-branding-card ${branding.data?.logo_custom ? 'is-custom' : ''}`}>
          <div className="tc-branding-preview">
            <img
              src={branding.data?.logo_url ?? '/api/branding/logo'}
              alt="Current site logo"
              className="tc-branding-logo"
            />
          </div>
          <div className="tc-branding-copy">
            <strong>Site logo</strong>
            <span>Shown in navigation and on the sign-in screen.</span>
            <span className="tc-branding-state">
              {branding.data?.logo_custom ? 'Custom artwork' : 'TeachCraftAI default'}
            </span>
          </div>
          <input
            ref={logoInput}
            type="file"
            accept="image/png"
            hidden
            onChange={(event) => choose('logo', event.target.files)}
          />
          <div className="tc-branding-actions">
            <Button onClick={() => logoInput.current?.click()} disabled={update.isPending}>
              {update.isPending ? 'Uploading…' : 'Replace logo'}
            </Button>
            {branding.data?.logo_custom && (
              <Button
                variant="ghost"
                onClick={() => restore.mutate('logo')}
                disabled={restore.isPending}
              >
                Restore
              </Button>
            )}
          </div>
        </article>
        <article className={`tc-branding-card ${branding.data?.favicon_custom ? 'is-custom' : ''}`}>
          <div className="tc-branding-preview">
            <img
              src={branding.data?.favicon_url ?? '/api/branding/favicon'}
              alt="Current favicon"
              className="tc-branding-favicon"
            />
          </div>
          <div className="tc-branding-copy">
            <strong>Favicon</strong>
            <span>Shown beside the page title in browser tabs.</span>
            <span className="tc-branding-state">
              {branding.data?.favicon_custom ? 'Custom artwork' : 'TeachCraftAI default'}
            </span>
          </div>
          <input
            ref={faviconInput}
            type="file"
            accept="image/png"
            hidden
            onChange={(event) => choose('favicon', event.target.files)}
          />
          <div className="tc-branding-actions">
            <Button
              onClick={() => faviconInput.current?.click()}
              disabled={update.isPending}
            >
              {update.isPending ? 'Uploading…' : 'Replace favicon'}
            </Button>
            {branding.data?.favicon_custom && (
              <Button
                variant="ghost"
                onClick={() => restore.mutate('favicon')}
                disabled={restore.isPending}
              >
                Restore
              </Button>
            )}
          </div>
        </article>
      </div>
    </section>
  )
}
